import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { api, ApiError, can, type MemberGroup } from '../lib/api'
import { useDialogGuard, useReturnFocus } from '../lib/sheet'

/** The categories and the areas — `PLAN.html` section 04, and the customer's
 *  own sentences for what they are for:
 *
 *    "Two groups that will be coordinator group and then area groups. So when
 *     I want to send out a broadcast, I can choose which area should receive
 *     it, all areas, only coordinators, coordinators and areas."
 *
 *    "It's two separate things, categories and areas... when you choose
 *     categories, you should see two categories available or three. When you
 *     choose areas, you should see areas, and it should be available to choose
 *     more than one area."
 *
 *  TWO KINDS, AND THEY ARE NOT THE SAME SORT OF THING. A category is what a
 *  member IS and he holds AT MOST ONE — asked directly, he chose "exactly
 *  one" — so the record draws them as radio buttons and the server refuses a
 *  second. An area is where he covers, and he holds as many as apply. This
 *  dialog is where both lists are kept; who is in them is set on the member.
 *
 *  FOUR SENTENCES, ONE LIST. `app/routes/member_groups.py` makes the same point
 *  from the server's side: "all areas", "only coordinators", "coordinators and
 *  areas" and "this area" are not four features, they are four selections from
 *  one list of groups. So this dialog manages the list and nothing else — no
 *  modes, no send path, no membership. Who is IN a group is ticked on the
 *  member's own record, where the person is in front of you.
 *
 *  THE TWO THINGS IT MUST NOT OFFER, because the server will not do them and a
 *  control drawn and then refused is worse than no control:
 *
 *  - A GROUP'S KIND CANNOT BE CHANGED. `GroupPatch` has no `kind`, and the
 *    reason is on the record rather than in the schema: every broadcast already
 *    aimed at that group was aimed at something else, so the record of who was
 *    asked to go would quietly start reading wrongly. The kind is chosen once,
 *    on the add form, and afterwards it is only ever a heading.
 *  - A GROUP IS RETIRED, NEVER DELETED. There is no DELETE route to call. Same
 *    reason, plus `member_group_members` is ON DELETE CASCADE — a delete would
 *    silently empty everybody's membership with no trace it had ever been set.
 *
 *  READING IS UNGATED, EVERY WRITE IS `settings.members`. That split is the
 *  route's, not this file's invention: choosing who a broadcast goes to is part
 *  of taking a call, and a dispatcher who cannot see which areas exist can only
 *  send to everybody. So a role without the key opens this and reads it, rather
 *  than pressing a button that answers 403. The roster's Groups button is drawn
 *  for anybody for that reason — it was gated on `settings.members` for an
 *  afternoon, which made every read-only path in this file dead code and gave
 *  the product the opposite of what the ungated route was for. The two halves
 *  are still separate jobs: the caller decides who may SEE a screen and the
 *  screen decides which controls it draws, and keeping both is what stops a
 *  permission from being enforced in exactly one place.
 */

/** The server's own ceiling on a group name — `GroupIn.name` and
 *  `GroupPatch.name` are both `max_length=80` — mirrored on both boxes that
 *  can reach them.
 *
 *  NOT DECORATION. A Pydantic length refusal comes back with a LIST in
 *  `detail`, and `request` in `lib/api.ts` hands `detail` straight to
 *  `ApiError`, which stringifies it — so an 81-character paste would put the
 *  literal words "[object Object]" in the notice at the top of this dialog,
 *  in place of the server's sentence the catch blocks below exist to show.
 *  `SiteNotes.tsx` mirrors `PlacePatch`'s 400 for exactly this reason.
 */
const NAME_MAX = 80

/** A rename in progress: which group, and what has been typed so far. */
type Editing = { id: string; name: string }

/** What a row can do. Bundled because every row needs all of it and threading
 *  seven props through two components is how one of them drifts. */
type Acts = {
  mayEdit: boolean
  /** A write is in flight. The rows do not disable anything themselves — the
   *  one fieldset does that — but the rename row has to know when a write
   *  FINISHED, to take the caret back off the disable. */
  busy: boolean
  editing: Editing | null
  setEditing: (next: Editing | null) => void
  startRename: (group: MemberGroup) => void
  rename: () => void
  setActive: (group: MemberGroup, on: boolean) => void
}

/** How many people it would reach, in words. ONE definition, used by the row
 *  and by the confirmation, so the number somebody reads before pressing Retire
 *  and the number in the question they are then asked cannot differ. */
function membersPhrase(n: number): string {
  if (n === 0) return 'nobody yet'
  return n === 1 ? '1 member' : `${n} members`
}

export function MemberGroupsDialog({ onClose, onChanged }: {
  onClose: () => void
  onChanged: () => void
}) {
  const [groups, setGroups] = useState<MemberGroup[]>([])
  const [loaded, setLoaded] = useState(false)
  // Whether the last read THREW, which is a different thing from an empty
  // list and cannot be told from one by looking at `groups`. See `load`.
  const [failed, setFailed] = useState(false)
  const [name, setName] = useState('')
  const [kind, setKind] = useState<'category' | 'area'>('area')
  const [editing, setEditing] = useState<Editing | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mayEdit = can('settings.members')
  useReturnFocus()

  /** RETIRED ONES INCLUDED, and there is deliberately no "Show retired"
   *  toggle here as there is on the settings lists. Two reasons: this list is
   *  a handful of rows rather than a page of them, and `UNIQUE
   *  (organization_id, name)` ignores `active` — so a retired group's name is
   *  still taken, and hiding it turns the 409 that follows into a refusal
   *  nobody can explain. */
  const load = useCallback(async () => {
    try {
      setGroups(await api.memberGroups(true))
      setError(null)
      setFailed(false)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the groups.')
      // A FETCH THAT THREW IS NOT AN EMPTY LIST. Both empty states below are
      // assertions about dispatch — "a broadcast cannot be aimed at the
      // coordinators alone", "every broadcast goes to the whole roster" — and
      // neither is known when nothing arrived: an empty `groups` after a
      // refusal or a dead connection is a claim nobody can tell apart from a
      // real one, which is the map's rule about a guessed pin at a different
      // boundary. `loaded` cannot carry this, and is deliberately still set
      // below: a dialog left on "Loading…" for ever is the other half of the
      // same lie.
      setFailed(true)
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // The group being renamed, as the server last returned it. It is what
  // decides whether there is anything to lose: an input opened and left
  // untouched is not unsaved work, and asking about it teaches people to click
  // through the guard.
  const editingGroup = editing ? groups.find((g) => g.id === editing.id) : undefined
  const renaming = !!editing && !!editingGroup
    && editing.name.trim() !== editingGroup.name
  const unsaved = name.trim() !== '' || renaming

  const { leave, backdrop } = useDialogGuard(
    () => (unsaved
      ? 'Discard what you have typed? Everything already saved stays as it is.'
      : null),
    onClose,
  )

  /** After a write that worked.
   *
   *  RE-READ, NEVER PATCHED IN PLACE. The server orders by kind, then
   *  `sort_order`, then name, so a rename MOVES a row — and re-sorting the
   *  list here would be a second definition of the order, disagreeing with the
   *  first the moment a name starts with an accent. It is a handful of rows;
   *  the fetch is cheaper than the bug.
   *
   *  Then the parent, because the roster behind this dialog prints group names
   *  on every row and draws its ticks from the same list. It reads both again
   *  rather than guessing what happened in here.
   */
  async function after() {
    await load()
    onChanged()
  }

  async function add(e: FormEvent) {
    e.preventDefault()
    const typed = name.trim()
    if (!typed) return
    setBusy(true)
    setError(null)
    try {
      // `sort_order` IS DELIBERATELY NOT SENT, so every group keeps the
      // server's default of 0 and the ordering falls through to the name.
      // Sending a count would make it creation order, which is an order
      // nobody can correct: there is no reorder control on this screen and
      // section 19 forbids inventing one for a distinction the product does
      // not draw.
      await api.memberGroupCreate({ name: typed, kind })
      // Cleared INSIDE the write. A duplicate name comes back 409 naming it,
      // and the words have to still be in the box to be corrected.
      setName('')
      await after()
    } catch (err) {
      // The server's own sentence — "There is already a group called
      // 'Ahuntsic'." — is better than anything this screen could say.
      setError(err instanceof ApiError ? err.message : 'Could not add that group.')
    } finally {
      setBusy(false)
    }
  }

  /** Start renaming a row, through the same question every other exit asks.
   *
   *  IT IS THE IDENTICAL UNSAVED WORK. `renaming` is a conjunct of `unsaved`,
   *  so closing the dialog with a half-typed name in the box asks before
   *  throwing it away — and pressing Rename one row further down used to
   *  discard exactly that state without a word. A guard that asks on one door
   *  and not on another, for one state, is the phone book's "four exits, one
   *  guard" written the wrong way round.
   *
   *  Cancel and Escape ON THE ROW ITSELF stay unguarded, and that is not the
   *  same thing: both mean "forget this name", said about the name in front of
   *  you. Pressing Rename elsewhere means "edit that one", and losing this one
   *  is a side effect nobody asked for.
   */
  function startRename(group: MemberGroup) {
    if (renaming && editingGroup && !confirm(
      `Discard the new name typed for ${editingGroup.name}?`
      + ' Everything already saved stays as it is.')) return
    setEditing({ id: group.id, name: group.name })
  }

  async function rename() {
    if (!editing) return
    const typed = editing.name.trim()
    if (!typed) return
    setBusy(true)
    setError(null)
    try {
      await api.memberGroupUpdate(editing.id, { name: typed })
      // Closed inside the write, for the same reason the add box is cleared
      // inside it: a clash leaves the input open with the rejected name still
      // in it.
      setEditing(null)
      await after()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not rename that group.')
    } finally {
      setBusy(false)
    }
  }

  async function setActive(group: MemberGroup, on: boolean) {
    const count = group.members ?? 0
    // ASKED ON THE WAY OUT, NOT ON THE WAY BACK. Retiring removes a way of
    // aiming a broadcast, which is not visible from this dialog at all —
    // whoever finds out is a dispatcher looking for an area at two in the
    // morning. Restoring costs nobody anything and is not worth a question.
    if (!on && !confirm(
      `Retire ${group.name}? A broadcast can no longer be aimed at it.`
      + (count > 0
        ? ` The ${membersPhrase(count)} in it stay on the roster and are still`
          + ' reached by every other group they are in.'
        : '')
      + ' Broadcasts already sent to it keep reading correctly, and it can be'
      + ' restored here.')) return
    setBusy(true)
    setError(null)
    try {
      await api.memberGroupUpdate(group.id, { active: on })
      await after()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that group.')
    } finally {
      setBusy(false)
    }
  }

  const acts: Acts = {
    mayEdit, busy, editing, setEditing, startRename, rename, setActive,
  }

  // FILTERED, NOT SORTED. The server's ORDER BY is kept inside each kind —
  // `filter` preserves it — and only the two kinds are separated, which is a
  // partition rather than a second ordering. They are drawn categories first
  // because that is the order the customer names them in and the order the
  // record's own tabs put them in; the server's own `ORDER BY g.kind` puts
  // 'area' first, which is alphabet rather than meaning.
  const categories = groups.filter((g) => g.kind === 'category')
  const areas = groups.filter((g) => g.kind === 'area')

  return (
    <div className="modalback" {...backdrop}>
      <div
        className="modal wide sheet-rec"
        role="dialog"
        aria-modal="true"
        aria-labelledby="memgroups"
      >
        <header className="sheet-rechead">
          <h3 id="memgroups">Groups</h3>
          {busy
            ? <span className="sheet-recstate">Working…</span>
            : !mayEdit && <span className="sheet-recstate">Read only</span>}
          <span className="spacer" />
          <button className="sheet-recx" type="button" aria-label="Close" onClick={leave}>
            ×
          </button>
        </header>

        <div className="sheet-recbody">
          {error && <div className="notice" role="alert">{error}</div>}

          <p className="blurb">
            Who a broadcast can be aimed at: one category, one area, several
            areas, or everybody. A member is in one category and as many areas
            as apply — a coordinator is also somebody’s neighbour, and being
            promoted must not stop him hearing about his own street.
          </p>

          {!mayEdit && (
            <p className="footnote">
              Changing these is part of editing the roster, which your role does
              not have. The list is readable by anybody, because choosing who a
              broadcast goes to is part of taking a call rather than a
              configuration task.
            </p>
          )}

          {/* ONE fieldset over the lot, `busy` included, and that is what
              serialises the writes. Rename and Retire fire straight from
              onClick, so without it two quick presses are two racing PATCHes
              against the same group — the failure the roster's number rows
              had. It is also how the whole screen goes read-only for a role
              that may look and not touch. */}
          <fieldset className="bare" disabled={!mayEdit || busy}>
            {mayEdit && (
              <>
                <form className="callbar" onSubmit={add}>
                  <input
                    className="grow"
                    value={name}
                    maxLength={NAME_MAX}
                    placeholder="Ahuntsic, Outremont, Coordinators…"
                    aria-label="Name of the new group"
                    onChange={(e) => setName(e.target.value)}
                  />
                  {/* NOT WRAPPED IN A LABEL. `.modal label select` makes a
                      select a full-width block, which is right for a stacked
                      form and wrong for a bar. */}
                  <select
                    className="pick"
                    value={kind}
                    aria-label="What kind of group this is"
                    onChange={(e) => setKind(e.target.value as 'category' | 'area')}
                  >
                    <option value="area">An area</option>
                    <option value="category">A category</option>
                  </select>
                  <button className="primary compact" disabled={busy || !name.trim()}>
                    Add
                  </button>
                </form>
                <p className="footnote">
                  Which kind it is is settled when it is made and cannot be
                  changed afterwards: every broadcast already aimed at a group
                  was aimed at what it was then, and the record of who was asked
                  to go would start reading wrongly. Retire it and make the
                  other one. Adding, renaming and retiring save the moment you
                  press the button — there is no Save on this dialog.
                </p>
              </>
            )}

            {!loaded ? (
              <div className="listempty">Loading…</div>
            ) : failed && groups.length === 0 ? (
              /* NOT DRAWN AS TWO EMPTY SECTIONS. Whether any categories or any
                 areas exist is precisely what did not arrive, so the
                 sentences under those headings would be inventing the answer.
                 The notice above says what went wrong; this says what it
                 means. A list that arrived and then failed to RE-read still
                 renders below, because those rows were really read. */
              <div className="listempty">
                The groups could not be read, so this is not an empty list —
                what exists is unknown until it loads. Close this and open it
                again.
              </div>
            ) : (
              <>
                <Section
                  title="Categories"
                  blurb="What a member is — two or three of them. Everybody is in one at most, so choosing one on his record un-chooses the other."
                  groups={categories}
                  empty="No categories yet — until there is one, a broadcast cannot be aimed at the coordinators alone."
                  acts={acts}
                />
                <Section
                  title="Areas"
                  blurb="As many as the organization is divided into. A broadcast can be aimed at one of them, at several, or at all of them at once."
                  groups={areas}
                  empty="No areas yet — until there is one, every broadcast goes to the whole roster."
                  acts={acts}
                />

                <p className="footnote">
                  The count beside each group is its <strong>active</strong>
                  {' '}members. A retired member is not reached by anything, so
                  counting them would say a broadcast goes further than it does.
                  Groups themselves are retired and never deleted, and both are
                  on this list: a broadcast sent to an area last month is the
                  record of who was asked to go, and the name stays taken, so a
                  hidden retired group is a duplicate-name refusal nobody can
                  explain.
                </p>
              </>
            )}
          </fieldset>
        </div>

        <div className="modalrow">
          <button className="ghost" type="button" onClick={leave}>Close</button>
        </div>
      </div>
    </div>
  )
}

function Section({
  title, blurb, groups, empty, acts,
}: {
  title: string
  blurb: string
  groups: MemberGroup[]
  empty: string
  acts: Acts
}) {
  return (
    <>
      <h4 className="sheet-recsub">{title}</h4>
      <p className="footnote">{blurb}</p>
      {groups.length === 0
        ? <div className="listempty">{empty}</div>
        : groups.map((g) => (acts.editing?.id === g.id
          ? <RenameRow key={g.id} group={g} acts={acts} />
          : <GroupRow key={g.id} group={g} acts={acts} />))}
    </>
  )
}

function GroupRow({ group, acts }: { group: MemberGroup; acts: Acts }) {
  // `active` is optional on the type, so absent means active — a row is never
  // rendered as retired because a field was missing from a response.
  const retired = group.active === false
  return (
    <div className={retired ? 'listrow retired' : 'listrow'}>
      <span className="nm">{group.name}</span>
      {retired && <span className="tag">retired</span>}
      <span className="spacer" />
      <span className="lbl">{membersPhrase(group.members ?? 0)}</span>
      {acts.mayEdit && (
        <button
          className="ghost tiny nogrow"
          type="button"
          onClick={() => acts.startRename(group)}
        >
          Rename
        </button>
      )}
      {acts.mayEdit && (
        <button
          className={retired ? 'ghost tiny nogrow ok' : 'ghost tiny nogrow'}
          type="button"
          onClick={() => acts.setActive(group, retired)}
        >
          {retired ? 'Restore' : 'Retire'}
        </button>
      )}
    </div>
  )
}

/** One row, while its name is being typed over.
 *
 *  A FORM, so Enter saves. The dialog's own root is a `div` rather than a form
 *  precisely so this can be one: the roster's record dialog learned that a bare
 *  Enter inside a nested control submits the wrong thing entirely.
 */
function RenameRow({ group, acts }: { group: MemberGroup; acts: Acts }) {
  const draft = acts.editing?.name ?? ''
  const input = useRef<HTMLInputElement | null>(null)

  // THE CARET COMES BACK WHEN A WRITE FINISHES. The one fieldset disables
  // everything while a write is in flight, this input included, and a disabled
  // input loses focus — so a rename refused as a duplicate leaves the box open
  // with the rejected name in it exactly as `rename` intends, and the person
  // correcting it has to click into it first to type. Only after a write that
  // finished: the opening case is `autoFocus`, and focusing on every render
  // would fight it and steal the caret back mid-word. A save that WORKED
  // unmounts this row before `busy` clears, so nothing is grabbed then.
  const wasBusy = useRef(false)
  useEffect(() => {
    if (wasBusy.current && !acts.busy) input.current?.focus()
    wasBusy.current = acts.busy
  }, [acts.busy])

  return (
    <form
      className="callbar"
      // The callbar's bottom margin is for a bar that sits ABOVE a list. This
      // one IS a row in the list, and 14px underneath it opens a gap that
      // reads as the end of the section.
      style={{ marginBottom: 0, padding: '4px 0' }}
      onSubmit={(e) => { e.preventDefault(); acts.rename() }}
      onKeyDown={(e) => {
        // ESCAPE MUST NOT REACH THE WINDOW. `useDialogGuard` listens there, so
        // the key that means "forget this name" would otherwise close the
        // whole dialog — and the guard would not even ask, because a rename it
        // is about to discard is exactly the unsaved work it reports. React
        // attaches its listeners to the root element, which is below `window`,
        // so stopping propagation here really does stop it.
        if (e.key !== 'Escape') return
        e.stopPropagation()
        acts.setEditing(null)
      }}
    >
      <input
        className="grow"
        ref={input}
        value={draft}
        autoFocus
        maxLength={NAME_MAX}
        aria-label={`Rename ${group.name}`}
        onChange={(e) => acts.setEditing({ id: group.id, name: e.target.value })}
      />
      <button className="ghost tiny nogrow" type="submit" disabled={!draft.trim()}>
        Save
      </button>
      <button
        className="ghost tiny nogrow"
        type="button"
        onClick={() => acts.setEditing(null)}
      >
        Cancel
      </button>
    </form>
  )
}
